import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { apiClient } from '@/api/axiosClient';
import PageHeader from '@/components/common/PageHeader';
import DataTable from '@/components/common/DataTable';
import type { DataTableColumn } from '@/components/common/DataTable';
import SearchableSelect from '@/components/common/SearchableSelect';
import StatusChip from '@/components/common/StatusChip';
import { GridContainer, GridItem } from '@/components/common/ResponsiveGrid';
import { useDateFormat } from '@/hooks/useDateFormat';
import { useProcessConfiguration } from '@/hooks/useProcessConfiguration';
import type {
  BatchOptionDto,
  BatchTraceabilityDto,
  BatchTraceabilityMovementRow,
  TraceabilityStepDto,
  TraceabilityResultDto,
} from '@/types/reports';
import { withId } from './reportHelpers';

// Split out of Inventory Reports into its own module — genealogy (full supplier<->customer chain)
// and single-batch ledger history are both "trace something", but neither is a stock-position
// report, so they read oddly bundled under Inventory once there were two of them.
const TABS = ['batchTraceability', 'traceability'] as const;
const TAB = Object.fromEntries(TABS.map((k, i) => [k, i])) as Record<(typeof TABS)[number], number>;

export default function TraceabilityReportsPage() {
  const { t } = useTranslation('reports');
  const { formatDate } = useDateFormat();
  const { data: processConfiguration } = useProcessConfiguration();
  const showClientColumn = Boolean(processConfiguration?.thirdPartyLogisticsEnabled);
  const [tab, setTab] = useState(0);
  const [batchId, setBatchId] = useState('');

  // `traceQuery` is what is being typed; `traceSubmitted` is what was actually searched — keeping
  // them apart is what stops the recursive walk running on every keystroke.
  const [traceQuery, setTraceQuery] = useState('');
  const [traceSubmitted, setTraceSubmitted] = useState('');
  const [traceDirection, setTraceDirection] = useState<'Forward' | 'Backward'>('Forward');
  const [traceBatchId, setTraceBatchId] = useState('');

  const { data: batchOptions = [] } = useQuery({
    queryKey: ['reports', 'batches', 'search'],
    queryFn: async () => (await apiClient.get<BatchOptionDto[]>('/reports/batches/search')).data,
    enabled: tab === TAB.batchTraceability,
  });
  const { data: trace, isLoading: loadingTrace } = useQuery({
    queryKey: ['reports', 'batch-traceability', batchId],
    queryFn: async () => (await apiClient.get<BatchTraceabilityDto>(`/reports/batch-traceability/${batchId}`)).data,
    enabled: tab === TAB.batchTraceability && Boolean(batchId),
  });

  // Only fires on an explicit Trace click — genealogy walks several tables recursively, so running
  // it on every keystroke would be wasteful and slow.
  const { data: trace2, isFetching: loadingTrace2 } = useQuery({
    queryKey: ['reports', 'traceability', traceSubmitted, traceDirection, traceBatchId],
    queryFn: async () => (await apiClient.get<TraceabilityResultDto>('/reports/traceability', {
      params: { query: traceSubmitted, direction: traceDirection, batchId: traceBatchId || undefined },
    })).data,
    enabled: tab === TAB.traceability && traceSubmitted.length > 0,
  });

  type TraceRow = ReturnType<typeof withId<TraceabilityStepDto>>[number];
  const traceabilityColumns: DataTableColumn<TraceRow>[] = [
    // Depth is rendered as indentation rather than a number — the shape of the chain is the point.
    {
      field: 'kind',
      headerName: t('columns.stage'),
      render: (r: TraceRow) => (
        <Box sx={{ pl: r.depth * 2 }}>
          <Typography variant="body2" fontWeight={r.depth === 0 ? 700 : 500}>{r.kind}</Typography>
        </Box>
      ),
    },
    { field: 'documentNumber', headerName: t('columns.document'), render: (r: TraceRow) => r.documentNumber ?? '—' },
    { field: 'materialCode', headerName: t('columns.materialCode'), render: (r: TraceRow) => r.materialCode ?? '—' },
    { field: 'batchNo', headerName: t('columns.batchNo'), render: (r: TraceRow) => r.batchNo ?? '—' },
    { field: 'qty', headerName: t('columns.qty'), render: (r: TraceRow) => (r.qty ?? '—') },
    { field: 'partyName', headerName: t('columns.party'), render: (r: TraceRow) => r.partyName ?? '—' },
    { field: 'date', headerName: t('columns.date'), render: (r: TraceRow) => (r.date ? formatDate(r.date) : '—') },
    { field: 'detail', headerName: t('columns.detail'), render: (r: TraceRow) => r.detail ?? '—' },
  ];

  const traceColumns: DataTableColumn<ReturnType<typeof withId<BatchTraceabilityMovementRow>>[number]>[] = [
    { field: 'transactionDate', headerName: t('columns.date'), render: (r) => new Date(r.transactionDate).toLocaleString() },
    { field: 'transactionType', headerName: t('columns.type'), render: (r) => <StatusChip status={r.transactionType} />, filterable: true },
    { field: 'warehouseName', headerName: t('columns.warehouse'), filterable: true },
    { field: 'locationCode', headerName: t('columns.location'), render: (r) => r.locationCode ?? '—' },
    { field: 'quantityIn', headerName: t('columns.in') },
    { field: 'quantityOut', headerName: t('columns.out') },
    { field: 'balanceQty', headerName: t('columns.balance') },
    { field: 'remarks', headerName: t('columns.remarks'), render: (r) => r.remarks ?? '—' },
  ];

  return (
    <>
      <PageHeader title={t('modules.traceability.title')} subtitle={t('modules.traceability.subtitle')} />

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }} variant="scrollable" scrollButtons="auto">
        {TABS.map((key) => (
          <Tab key={key} label={t(`${key}.title`)} />
        ))}
      </Tabs>

      <Box hidden={tab !== TAB.traceability}>
        <Stack spacing={2}>
          <Alert severity="info">{t('traceability.hint')}</Alert>

          <Stack direction="row" spacing={2} alignItems="flex-start" flexWrap="wrap">
            <TextField
              size="small"
              label={t('traceability.query')}
              value={traceQuery}
              onChange={(e) => setTraceQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setTraceBatchId('');
                  setTraceSubmitted(traceQuery.trim());
                }
              }}
              sx={{ minWidth: 260 }}
              helperText={t('traceability.queryHelper')}
            />
            <ToggleButtonGroup
              color="primary"
              exclusive
              size="small"
              value={traceDirection}
              onChange={(_e: React.MouseEvent<HTMLElement>, v: 'Forward' | 'Backward' | null) => v && setTraceDirection(v)}
              sx={{ mt: 0.5 }}
            >
              <ToggleButton value="Forward">{t('traceability.forward')}</ToggleButton>
              <ToggleButton value="Backward">{t('traceability.backward')}</ToggleButton>
            </ToggleButtonGroup>
            <Button
              variant="contained"
              sx={{ mt: 0.5 }}
              disabled={!traceQuery.trim()}
              onClick={() => { setTraceBatchId(''); setTraceSubmitted(traceQuery.trim()); }}
            >
              {t('traceability.trace')}
            </Button>
          </Stack>

          {/* A batch number can name several materials in this build, so nothing is traced until the
              operator says which one they meant — guessing would produce a confident wrong answer. */}
          {trace2?.ambiguous ? (
            <Alert severity="warning">
              <Typography variant="body2" sx={{ mb: 1 }}>{t('traceability.ambiguous')}</Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {(trace2.candidates ?? []).map((c) => (
                  <Button key={c.batchId} size="small" variant="outlined" onClick={() => setTraceBatchId(c.batchId)}>
                    {c.materialCode} — {c.materialName}
                  </Button>
                ))}
              </Stack>
            </Alert>
          ) : undefined}

          {traceSubmitted && !loadingTrace2 && trace2 && !trace2.found && !trace2.ambiguous ? (
            <Alert severity="error">{t('traceability.notFound', { query: traceSubmitted })}</Alert>
          ) : undefined}

          {trace2?.found ? (
            <>
              <Typography variant="body2" color="text.secondary">
                {t('traceability.resolved', {
                  material: `${trace2.rootMaterialCode} — ${trace2.rootMaterialName}`,
                  batch: trace2.rootBatchNo,
                  via: trace2.resolvedFrom,
                })}
              </Typography>
              <DataTable
                columns={traceabilityColumns}
                rows={withId(
                  trace2.steps.map((r, i) => ({ ...r, _index: i })),
                  (r) => `${r._index}-${r.kind}-${r.batchId ?? ''}`,
                )}
                loading={loadingTrace2}
                exportFileName="traceability"
              />
            </>
          ) : undefined}
        </Stack>
      </Box>

      <Box hidden={tab !== TAB.batchTraceability}>
        <Box sx={{ maxWidth: 480, mb: 3 }}>
          <SearchableSelect
            label={t('batchTraceability.selectBatch')} value={batchId} onChange={setBatchId} fullWidth
            options={batchOptions.map((b) => ({ value: b.batchId, label: `${b.batchNo} — ${b.materialCode} (${b.materialName})` }))}
          />
        </Box>

        {trace && (
          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <GridContainer columns={4} spacing={2}>
                <GridItem xs={4} sm={2} md={1}>
                  <Typography variant="caption" color="text.secondary">{t('batchTraceability.batchNo')}</Typography>
                  <Typography variant="subtitle1">{trace.batchNo}</Typography>
                </GridItem>
                <GridItem xs={4} sm={2} md={1}>
                  <Typography variant="caption" color="text.secondary">{t('columns.materialName')}</Typography>
                  <Typography variant="subtitle1">{trace.materialCode} — {trace.materialName}</Typography>
                </GridItem>
                <GridItem xs={4} sm={2} md={1}>
                  <Typography variant="caption" color="text.secondary">{t('columns.status')}</Typography>
                  <Stack direction="row"><StatusChip status={trace.status} /></Stack>
                </GridItem>
                <GridItem xs={4} sm={2} md={1}>
                  <Typography variant="caption" color="text.secondary">{t('batchTraceability.currentOnHand')}</Typography>
                  <Typography variant="subtitle1">{trace.currentOnHand}</Typography>
                </GridItem>
                <GridItem xs={4} sm={2} md={1}>
                  <Typography variant="caption" color="text.secondary">{t('columns.mfgDate')}</Typography>
                  <Typography variant="subtitle1">{trace.mfgDate ? formatDate(trace.mfgDate) : '—'}</Typography>
                </GridItem>
                <GridItem xs={4} sm={2} md={1}>
                  <Typography variant="caption" color="text.secondary">{t('columns.expiryDate')}</Typography>
                  <Typography variant="subtitle1">{trace.expiryDate ? formatDate(trace.expiryDate) : '—'}</Typography>
                </GridItem>
                {showClientColumn && (
                  <GridItem xs={4} sm={2} md={1}>
                    <Typography variant="caption" color="text.secondary">{t('columns.client')}</Typography>
                    <Typography variant="subtitle1">{trace.clientName ?? '—'}</Typography>
                  </GridItem>
                )}
              </GridContainer>
            </CardContent>
          </Card>
        )}

        {batchId && (
          <DataTable
            columns={traceColumns}
            rows={withId(trace?.movements ?? [], (r) => `${r.transactionDate}-${r.transactionType}-${r.locationCode ?? ''}`)}
            loading={loadingTrace}
            searchable
            exportFileName="batch-traceability"
          />
        )}
      </Box>
    </>
  );
}
